import Switch from '@/common/components/atom/switch';
import Skeleton from '@/common/components/atom/skeleton';
import { SettingsActionRow, SettingsSection } from '@/common/components/settings/settingsList';
import { RefreshCw } from 'lucide-react';
import classNames from '@/common/utils/classNames';
import { ReactNode, TextareaHTMLAttributes } from 'react';

export const toFaDigits = (value: string | number) => String(value).replace(/\d/g, d => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);

/** Telegram-style switch in a row's end slot; the row itself toggles too. */
export const RowSwitch = ({
  checked,
  label,
  disabled,
  onChange,
}: {
  checked: boolean;
  label: string;
  disabled?: boolean;
  onChange: (on: boolean) => void;
}) => (
  <span onClick={event => event.stopPropagation()}>
    <Switch checked={checked} disabled={disabled} aria-label={label} onChange={event => onChange(event.target.checked)} />
  </span>
);

/** Borderless multi-line field row, the textarea twin of SettingsInput. */
export const SettingsTextArea = ({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) => (
  <label className="flex px-4 py-3">
    <textarea
      rows={4}
      {...rest}
      className={classNames(
        'min-w-0 flex-1 resize-none bg-transparent text-[15px] leading-7 text-[#222] outline-none focus:outline-none focus-visible:outline-none placeholder:text-[#a3a3a3]',
        className,
      )}
    />
  </label>
);

export const RowsSkeleton = ({ rows = 3 }: { rows?: number }) => (
  <SettingsSection>
    {Array.from({ length: rows }, (_, i) => (
      <div key={i} className="flex min-h-[54px] items-center gap-4 px-4">
        <Skeleton w="26px" h="26px" rounded="full" />
        <Skeleton w="45%" h="0.875rem" rounded="md" />
      </div>
    ))}
  </SettingsSection>
);

/** A number with − / + buttons, for hours and days. */
export const Stepper = ({
  value,
  min,
  max,
  unit,
  onChange,
  label,
}: {
  value: number;
  min: number;
  max?: number;
  unit: string;
  onChange: (value: number) => void;
  label: string;
}) => (
  <div className="flex items-center gap-3" aria-label={label}>
    <button
      type="button"
      aria-label="کم کردن"
      disabled={value <= min}
      onClick={() => onChange(Math.max(min, value - 1))}
      className="relative flex h-8 w-8 items-center justify-center rounded-full bg-[#F2F2F2] text-[18px] text-[#222] before:absolute before:-inset-1.5 before:content-[''] disabled:opacity-40"
    >
      −
    </button>
    <span className="min-w-[56px] text-center text-[15px] text-[#222]">
      {toFaDigits(value)} {unit}
    </span>
    <button
      type="button"
      aria-label="زیاد کردن"
      disabled={max !== undefined && value >= max}
      onClick={() => onChange(max === undefined ? value + 1 : Math.min(max, value + 1))}
      className="relative flex h-8 w-8 items-center justify-center rounded-full bg-[#F2F2F2] text-[18px] text-[#222] before:absolute before:-inset-1.5 before:content-[''] disabled:opacity-40"
    >
      +
    </button>
  </div>
);

export const Note = ({ children }: { children: ReactNode }) => (
  <p className="px-4 pb-3 text-[12.5px] leading-5 text-[#767c86]">{children}</p>
);

/** Shown instead of a form when the chat service didn't answer (offline, signed out of the chat service). */
export const LoadFailed = ({ onRetry }: { onRetry: () => void }) => (
  <SettingsSection footer="سرویس گفت‌وگو جواب نداد. اینترنت را بررسی کنید و دوباره تلاش کنید.">
    <SettingsActionRow icon={RefreshCw} title="تلاش دوباره" onClick={onRetry} />
  </SettingsSection>
);
